/**
 * Isomorphic hashing on WebCrypto (Node ≥ 20 and every modern browser).
 * All inputs/outputs are lowercase hex strings unless stated otherwise.
 */
const encoder = new TextEncoder();
function subtle() {
    const c = globalThis.crypto;
    if (!c?.subtle)
        throw new Error("WebCrypto (crypto.subtle) is not available in this runtime");
    return c.subtle;
}
export function bytesToHex(bytes) {
    const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    let out = "";
    for (const b of view)
        out += b.toString(16).padStart(2, "0");
    return out;
}
export function hexToBytes(hex) {
    if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex))
        throw new Error("Invalid hex string");
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++)
        out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return out;
}
export function utf8(text) {
    return encoder.encode(text);
}
/** SHA-256 of a UTF-8 string (or raw bytes) → hex. */
export async function sha256(data) {
    const bytes = typeof data === "string" ? utf8(data) : data;
    return bytesToHex(await subtle().digest("SHA-256", bytes));
}
/** SHA-512 of a UTF-8 string (or raw bytes) → hex. */
export async function sha512(data) {
    const bytes = typeof data === "string" ? utf8(data) : data;
    return bytesToHex(await subtle().digest("SHA-512", bytes));
}
/** HMAC-SHA256(key, message) → hex. Key and message are UTF-8 strings (hex seeds are used as text, Bustabit-style). */
export async function hmacSha256(key, message) {
    const keyBytes = typeof key === "string" ? utf8(key) : key;
    const msgBytes = typeof message === "string" ? utf8(message) : message;
    const cryptoKey = await subtle().importKey("raw", keyBytes, { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    return bytesToHex(await subtle().sign("HMAC", cryptoKey, msgBytes));
}
/** Cryptographically random hex string of `bytes` bytes (default 32 → 64 hex chars). */
export function randomHex(bytes = 32) {
    const buf = new Uint8Array(bytes);
    globalThis.crypto.getRandomValues(buf);
    return bytesToHex(buf);
}
//# sourceMappingURL=crypto.js.map